import { useMemo, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { Clock, Edit3, Plus, Trash2, Truck } from 'lucide-react-native';

import { Badge, Button, Card, EmptyState, SkeletonRows, StatTile, TextField, Sheet, Select } from '@/components/ui';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import {
  proveedoresQuery,
  upsertProveedor,
  eliminarProveedor,
} from '@/services/firebase/proveedores';
import { productosVentaQuery, insumosInternosQuery } from '@/services/firebase/inventario';
import { useAuth, selectIsAdmin } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { whatsappLink, normalizarWhatsappBO } from '@/utils/format';
import type { Proveedor } from '@/types/domain';

export function ProveedoresScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const isAdmin = useAuth(selectIsAdmin);
  const { data: proveedores, loading } = useFirestoreCollection(proveedoresQuery);
  const ventas = useFirestoreCollection(productosVentaQuery);
  const insumos = useFirestoreCollection(insumosInternosQuery);

  const [editing, setEditing] = useState<Proveedor | null>(null);
  const [open, setOpen] = useState(false);

  const productosPorProveedor = useMemo(() => {
    const map = new Map<string, number>();
    [...ventas.data, ...insumos.data].forEach((p) => {
      if (p.proveedorId) map.set(p.proveedorId, (map.get(p.proveedorId) ?? 0) + 1);
    });
    return map;
  }, [ventas.data, insumos.data]);

  const onEdit = (p: Proveedor) => {
    setEditing(p);
    setOpen(true);
  };
  const onNew = () => {
    setEditing(null);
    setOpen(true);
  };

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 20, paddingBottom: 80 }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
        <StatTile
          label="Proveedores"
          value={String(proveedores.length)}
          accent="magenta"
          icon={<Truck size={20} color={colors.magenta[700]} />}
          hint="Activos"
        />
        <StatTile
          label="Productos vinculados"
          value={String([...productosPorProveedor.values()].reduce((a, b) => a + b, 0))}
          accent="success"
          hint="A inventario actual"
        />
      </View>

      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <View>
            <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
              Directorio
            </Text>
            <Text style={{ color: colors.ink[500], fontSize: 13, marginTop: 2 }}>
              Cada proveedor tiene WhatsApp directo desde las alertas de stock bajo.
            </Text>
          </View>
          {isAdmin ? <Button label="Nuevo proveedor" iconLeft={<Plus size={16} color={colors.white} />} onPress={onNew} /> : null}
        </View>
      </Card>

      {loading ? (
        <SkeletonRows rows={3} />
      ) : proveedores.length === 0 ? (
        <Card>
          <EmptyState
            illustration="note"
            title="Aún no hay proveedores"
            message={isAdmin ? 'Cargá tu primer proveedor para vincularlo con productos y reponer en un toque.' : 'La administradora aún no cargó proveedores.'}
            actionLabel={isAdmin ? 'Cargar proveedor' : undefined}
            onAction={isAdmin ? onNew : undefined}
          />
        </Card>
      ) : (
        <View style={{ gap: 8 }}>
          {proveedores.map((p) => (
            <ProveedorRow
              key={p.id}
              proveedor={p}
              productos={productosPorProveedor.get(p.id) ?? 0}
              canEdit={isAdmin}
              onEdit={() => onEdit(p)}
              onDelete={async () => {
                await eliminarProveedor(p.id);
              }}
            />
          ))}
        </View>
      )}

      <ProveedorSheet
        visible={open}
        onClose={() => setOpen(false)}
        proveedor={editing}
      />
    </ScrollView>
  );
}

function ProveedorRow({
  proveedor,
  productos,
  canEdit,
  onEdit,
  onDelete,
}: {
  proveedor: Proveedor;
  productos: number;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
        padding: 16,
        backgroundColor: colors.white,
        borderRadius: radius['2xl'],
        borderWidth: 1,
        borderColor: colors.ink[100],
      }}
    >
      <View
        style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          backgroundColor: colors.magenta[100],
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Truck size={20} color={colors.magenta[700]} />
      </View>
      <View style={{ flex: 1, gap: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text style={{ fontWeight: typography.weights.bold, color: colors.ink[900], fontSize: 15 }}>{proveedor.nombre}</Text>
          {hoyNoAtiende(proveedor) ? <Badge label="Hoy no atiende" tone="warning" /> : null}
        </View>
        <Text style={{ fontSize: 12, color: colors.ink[500] }}>{telefonoLocal(proveedor.whatsapp)}</Text>
        {(proveedor.horaApertura || proveedor.horaCierre || proveedor.horarios) ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Clock size={12} color={colors.ink[500]} />
            <Text style={{ fontSize: 12, color: colors.ink[600] }} numberOfLines={1}>
              {horariosLegibles(proveedor.horaApertura, proveedor.horaCierre) || proveedor.horarios}
            </Text>
          </View>
        ) : null}
      </View>
      <Badge label={`${productos} productos`} tone="neutral" />
      {hoyNoAtiende(proveedor) ? null : (
        <Pressable
          onPress={() => Linking.openURL(whatsappLink(proveedor.whatsapp, `Hola ${proveedor.nombre}, te escribo desde CliniCAT.`))}
          accessibilityLabel="WhatsApp"
          style={({ hovered }) => ({
            width: 34,
            height: 34,
            borderRadius: 12,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: hovered ? '#10AC84' : '#25D366',
            // @ts-ignore web only
            cursor: 'pointer',
          })}
        >
          <WhatsAppIcon size={18} color={colors.white} />
        </Pressable>
      )}
      {canEdit ? (
        <>
          <Pressable
            onPress={onEdit}
            style={({ hovered }) => ({
              width: 34,
              height: 34,
              borderRadius: 12,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: hovered ? colors.ink[100] : 'transparent',
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            <Edit3 size={16} color={colors.ink[600]} />
          </Pressable>
          <Pressable
            onPress={onDelete}
            style={({ hovered }) => ({
              width: 34,
              height: 34,
              borderRadius: 12,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: hovered ? colors.danger[50] : 'transparent',
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            <Trash2 size={16} color={colors.danger[600]} />
          </Pressable>
        </>
      ) : null}
    </View>
  );
}

const DIAS_SEMANA = [
  { id: 1, label: 'Lun' },
  { id: 2, label: 'Mar' },
  { id: 3, label: 'Mié' },
  { id: 4, label: 'Jue' },
  { id: 5, label: 'Vie' },
  { id: 6, label: 'Sáb' },
  { id: 0, label: 'Dom' },
];

function hoyNoAtiende(p: Proveedor): boolean {
  if (!p.diasNoAtiende?.length) return false;
  return p.diasNoAtiende.includes(new Date().getDay());
}

/** Saca el prefijo +591 y devuelve el número local. */
function telefonoLocal(whatsapp: string | null | undefined): string {
  if (!whatsapp) return '';
  return whatsapp.replace(/^\+?591/, '').replace(/\D/g, '');
}

function horariosLegibles(apertura?: string, cierre?: string): string {
  if (apertura && cierre) return `${apertura} – ${cierre}`;
  if (apertura) return `Desde ${apertura}`;
  if (cierre) return `Hasta ${cierre}`;
  return '';
}

function ProveedorSheet({ visible, onClose, proveedor }: { visible: boolean; onClose: () => void; proveedor: Proveedor | null }) {
  const [nombre, setNombre] = useState(proveedor?.nombre ?? '');
  const [whatsapp, setWhatsapp] = useState(
    proveedor?.whatsapp ? proveedor.whatsapp.replace(/^\+?591/, '').replace(/\D/g, '') : '',
  );
  const [email, setEmail] = useState(proveedor?.email ?? '');
  const [notas, setNotas] = useState(proveedor?.notas ?? '');
  const [horaApertura, setHoraApertura] = useState(proveedor?.horaApertura ?? '');
  const [horaCierre, setHoraCierre] = useState(proveedor?.horaCierre ?? '');
  const [diasNoAtiende, setDiasNoAtiende] = useState<number[]>(proveedor?.diasNoAtiende ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset al abrir/cerrar
  useMemo(() => {
    setNombre(proveedor?.nombre ?? '');
    setWhatsapp(
      proveedor?.whatsapp ? proveedor.whatsapp.replace(/^\+?591/, '').replace(/\D/g, '') : '',
    );
    setEmail(proveedor?.email ?? '');
    setNotas(proveedor?.notas ?? '');
    setHoraApertura(proveedor?.horaApertura ?? '');
    setHoraCierre(proveedor?.horaCierre ?? '');
    setDiasNoAtiende(proveedor?.diasNoAtiende ?? []);
    setError(null);
  }, [proveedor, visible]);

  const toggleDia = (id: number) => {
    setDiasNoAtiende((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));
  };

  const onSave = async () => {
    setError(null);
    if (!nombre.trim()) return setError('El nombre es obligatorio.');
    if (!whatsapp.trim()) return setError('El WhatsApp es obligatorio.');
    const limpio = whatsapp.replace(/\D/g, '');
    if (limpio.length < 7) return setError('Número de WhatsApp inválido. Ej: 60008504');
    setSaving(true);
    try {
      await upsertProveedor({
        id: proveedor?.id,
        nombre: nombre.trim(),
        whatsapp: normalizarWhatsappBO(limpio),
        email: email.trim() || undefined,
        notas: notas.trim() || undefined,
        horaApertura: horaApertura.trim() || undefined,
        horaCierre: horaCierre.trim() || undefined,
        horarios: horariosLegibles(horaApertura, horaCierre) || undefined,
        diasNoAtiende: diasNoAtiende.length ? diasNoAtiende : undefined,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      title={proveedor ? 'Editar proveedor' : 'Nuevo proveedor'}
      subtitle="Los productos vinculados podrán contactarse por WhatsApp directo"
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button label={proveedor ? 'Guardar cambios' : 'Crear proveedor'} onPress={onSave} loading={saving} />
        </>
      }
    >
      <TextField label="Nombre" placeholder="VetLab Bolivia" value={nombre} onChangeText={setNombre} />

      <TextField
        label="WhatsApp"
        placeholder="60008504"
        value={whatsapp}
        onChangeText={(t) => setWhatsapp(t.replace(/\D/g, ''))}
        keyboardType="phone-pad"
        hint="Solo los dígitos del número local."
      />

      <TextField label="Email" placeholder="ventas@proveedor.bo" value={email} onChangeText={setEmail} keyboardType="email-address" autoCapitalize="none" />

      <View style={{ gap: 6 }}>
        <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>
          Horarios de atención
        </Text>
        <Text style={{ fontSize: 12, color: colors.ink[500] }}>
          De qué hora a qué hora atiende.
        </Text>
        <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center', marginTop: 4 }}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, marginBottom: 4 }}>
              DESDE
            </Text>
            {Platform.OS === 'web' ? (
              // @ts-ignore web-only native input
              <input
                type="time"
                value={horaApertura}
                onChange={(e: any) => setHoraApertura(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: `1.5px solid ${colors.ink[200]}`,
                  background: colors.white,
                  color: colors.ink[900],
                  fontSize: 15,
                  fontFamily: 'inherit',
                  outline: 'none',
                  height: 50,
                  boxSizing: 'border-box',
                }}
              />
            ) : (
              <TextField placeholder="08:00" value={horaApertura} onChangeText={setHoraApertura} />
            )}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, marginBottom: 4 }}>
              HASTA
            </Text>
            {Platform.OS === 'web' ? (
              // @ts-ignore web-only native input
              <input
                type="time"
                value={horaCierre}
                onChange={(e: any) => setHoraCierre(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: `1.5px solid ${colors.ink[200]}`,
                  background: colors.white,
                  color: colors.ink[900],
                  fontSize: 15,
                  fontFamily: 'inherit',
                  outline: 'none',
                  height: 50,
                  boxSizing: 'border-box',
                }}
              />
            ) : (
              <TextField placeholder="18:00" value={horaCierre} onChangeText={setHoraCierre} />
            )}
          </View>
        </View>
      </View>

      <View style={{ gap: 6 }}>
        <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>
          Días que NO atiende
        </Text>
        <Text style={{ fontSize: 12, color: colors.ink[500] }}>
          Tocá los días en los que no responde. Te avisamos cuando intentes contactarlo.
        </Text>
        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
          {DIAS_SEMANA.map((d) => {
            const active = diasNoAtiende.includes(d.id);
            return (
              <Pressable
                key={d.id}
                onPress={() => toggleDia(d.id)}
                style={({ hovered }) => ({
                  paddingHorizontal: 14,
                  height: 38,
                  borderRadius: radius.full,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: active
                    ? colors.warning[500]
                    : hovered
                      ? colors.crema[100]
                      : colors.crema[50],
                  borderWidth: 1.5,
                  borderColor: active ? colors.warning[600] : colors.ink[200],
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: typography.weights.bold,
                    color: active ? colors.white : colors.ink[700],
                  }}
                >
                  {d.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>

      <TextField label="Notas" placeholder="Condiciones de pago, contacto alternativo…" value={notas} onChangeText={setNotas} multiline />
      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}
